/**
 * Client "Requests" tab — the pre-report pipeline the Reports list
 * deliberately hides. Lists the team's report requests (basic facts +
 * client-language status); rows open the request detail, whose main
 * content is the conversation with GTI.
 */

import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

import { CountryPill, DueCell, StatusBadge, TruncatingPill } from '@/components';
import { useAuth } from '@/contexts/AuthContext';
import { useClientReportRequests, useSearchDraft } from '@/hooks';
import { isClientAnalyst } from '@/types/auth';
import {
  CLIENT_REQUEST_FILTER_OPTIONS,
  ClientReportRequestSummary,
  ClientRequestStatusFilter,
  displayedRequestStatus,
} from '@/types/report-request';
import { formatMoney } from '@/utils';

const STATUS_OPTIONS = CLIENT_REQUEST_FILTER_OPTIONS;

function isStatusFilter(value: string | null): value is ClientRequestStatusFilter {
  return value !== null && STATUS_OPTIONS.some((o) => o.value === value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '-';
  }
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const PAGE_SIZE = 25;

export function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const q = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const status = isStatusFilter(statusParam) ? statusParam : undefined;

  const updateParams = useCallback(
    (updates: Record<string, string | number>, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === '' || value === 0) {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          return next;
        },
        opts?.replace ? { replace: true } : undefined
      );
    },
    [setSearchParams]
  );

  // Debounced draft → one settled URL commit (replace, page reset);
  // the fetch keys on the committed value only.
  const [searchValue, setSearchValue] = useSearchDraft(q, (settled) =>
    updateParams({ q: settled, page: 1 }, { replace: true })
  );

  const { items, total, isLoading, error } = useClientReportRequests({
    page,
    limit: PAGE_SIZE,
    q: q || undefined,
    status,
  });
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasFilters = Boolean(q || status);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>Requests</Title>
          <Text size="sm" c="dimmed">
            Report requests we are reviewing or preparing for you. Started requests link to their
            report.
          </Text>
        </Stack>

        <Group gap="xs" wrap="nowrap">
          <TextInput
            placeholder="Search reference, subject, report type or requester…"
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 180 }}
            data-testid="requests-search"
          />
          <Select
            placeholder="Status"
            data={STATUS_OPTIONS}
            value={status ?? null}
            onChange={(value) => updateParams({ status: value ?? '', page: 1 })}
            clearable
            w={150}
            style={{ flexShrink: 0 }}
            data-testid="requests-filter-status"
          />
          {hasFilters && (
            <Button
              variant="subtle"
              size="compact-sm"
              c="dimmed"
              style={{ flexShrink: 0 }}
              onClick={() => {
                setSearchValue('');
                updateParams({ q: '', status: '', page: 1 });
              }}
              data-testid="requests-clear-filters"
            >
              Clear
            </Button>
          )}
        </Group>

        <Card shadow="sm" padding="lg" radius="md">
          <RequestsCardContent
            items={items}
            isLoading={isLoading}
            error={error}
            hasFilters={hasFilters}
          />
        </Card>

        {totalPages > 1 && (
          <Group justify="flex-end">
            <Pagination
              value={page}
              onChange={(p) => updateParams({ page: p })}
              total={totalPages}
              size="sm"
            />
          </Group>
        )}
      </Stack>
    </Container>
  );
}

function RequestsCardContent({
  items,
  isLoading,
  error,
  hasFilters,
}: Readonly<{
  items: ClientReportRequestSummary[];
  isLoading: boolean;
  error: Error | null;
  hasFilters: boolean;
}>) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Client analysts never see money (the API nulls it too) — drop the
  // column rather than render a dash-filled one.
  const showPrice = !isClientAnalyst(user);

  if (isLoading && items.length === 0) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h={300}>
        <Text c="red">{error.message}</Text>
      </Center>
    );
  }

  if (items.length === 0) {
    return (
      <Center h={300}>
        <Text c="dimmed">
          {hasFilters ? 'No requests match your filters.' : 'No report requests yet.'}
        </Text>
      </Center>
    );
  }

  return (
    // Fixed layout so a single long value can never blow the columns
    // out of the card; every free-text cell truncates with a hover
    // title instead.
    <Table striped highlightOnHover layout="fixed">
      <Table.Thead>
        <Table.Tr>
          <Table.Th w="12%">Reference</Table.Th>
          <Table.Th w="12%">Report Type</Table.Th>
          <Table.Th w="21%">Subjects</Table.Th>
          <Table.Th w="11%">Jurisdictions</Table.Th>
          <Table.Th w="12%">Status</Table.Th>
          <Table.Th w="10%">Requested by</Table.Th>
          {showPrice && <Table.Th w="7%">Price</Table.Th>}
          <Table.Th w={showPrice ? '7%' : '14%'}>Due</Table.Th>
          <Table.Th w="8%">Requested</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.map((request) => {
          // Once a client-visible report exists, its coarse status is
          // hoisted onto the row (the report is the thing progressing).
          const status = displayedRequestStatus(request);
          const isEnquiry = request.kind === 'enquiry';
          return (
            <Table.Tr
              key={request.id}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                navigate(
                  isEnquiry ? `/requests/enquiries/${request.id}` : `/requests/${request.id}`
                )
              }
              data-testid={`request-row-${request.id}`}
            >
              <Table.Td>
                {/* Triaged rows show the client's own reference (codenames
                    are admin-only); an enquiry's codename IS its client
                    handle, so enquiry rows keep it. */}
                <Text
                  size="sm"
                  fw={500}
                  truncate
                  title={(isEnquiry ? request.codename : request.clientReference) ?? undefined}
                >
                  {(isEnquiry ? request.codename : request.clientReference) ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                {isEnquiry ? (
                  <Badge size="sm" variant="outline" color="grape">
                    Enquiry
                  </Badge>
                ) : (
                  <Text size="sm" truncate title={request.configName ?? undefined}>
                    {request.configName ?? '—'}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="wrap">
                  {request.subjects.map((s) => (
                    <TruncatingPill key={s} label={s} />
                  ))}
                </Group>
              </Table.Td>
              <Table.Td>
                {request.jurisdictions.length ? (
                  // Flag + code badges (full name on hover) — the
                  // densest form for a column that can carry several.
                  <Group gap={4} wrap="wrap">
                    {request.jurisdictions.map((j) => (
                      <CountryPill key={j} country={j} />
                    ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    —
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Group gap={6} wrap="wrap">
                  <StatusBadge color={status.color} label={status.label} />
                  {request.expedited && (
                    <Badge size="sm" variant="light" color="orange">
                      Expedited
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm" truncate title={request.requesterEmail ?? undefined}>
                  {request.requesterEmail ?? '—'}
                </Text>
              </Table.Td>
              {showPrice && (
                <Table.Td>
                  <Text
                    size="sm"
                    c={formatMoney(request.price, request.currency) ? undefined : 'dimmed'}
                  >
                    {formatMoney(request.price, request.currency) ?? '—'}
                  </Text>
                </Table.Td>
              )}
              <Table.Td>
                <DueCell dueAt={request.dueAt} turnaroundTimeDays={request.turnaroundTimeDays} />
              </Table.Td>
              <Table.Td>
                <Text size="sm" truncate title={formatDate(request.createdAt)}>
                  {formatDate(request.createdAt)}
                </Text>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
